import { useState, useEffect, useRef } from "react";
import { useLoaderData } from "react-router-dom";
import Lottie from "lottie-react";
import PigAnimation from "../../assets/lottie/pig.json";
import { ReactComponent as IconCarrot } from "../../assets/icons/carrot.svg";
import { Popup } from "../popup/popup.view";
import "./newsletter.style.scss";

export const Newsletter = ({ data }) => {
    const loaderData = useLoaderData();
    const config = data || loaderData?.newsletter;

    // Struktureller Default (kein Text aus landing.json); wird als
    // Hook-Dependency schon vor dem Config-Guard gebraucht.
    const fallbackFormHeight = config?.fallbackFormHeight ?? "44rem";

    const [isOpenedPopup, setIsOpenedPopup] = useState(false);
    const frameRef = useRef(null);
    const openPopupHandler = () => {
        setIsOpenedPopup(true);
    }
    const closePopupHandler = () => {
        setIsOpenedPopup(false);
    }

    useEffect(() => {
        const scriptId = "mailjet-embedded-js";
        if (!document.getElementById(scriptId)) {
            const script = document.createElement("script");
            script.id = scriptId;
            script.src = "https://app.mailjet.com/pas-nc-embedded-v2.js";
            script.async = true;
            document.body.appendChild(script);
        }
    }, []);

    // Das Mailjet-Skript bindet sich nur einmal beim window-load-Event an die
    // zu diesem Zeitpunkt vorhandenen iframes. Unser iframe entsteht erst beim
    // Öffnen des Popups und muss deshalb selbst angemeldet werden.
    useEffect(() => {
        if (!isOpenedPopup) return undefined;

        let attempts = 0;
        let timer;
        const attachResizer = () => {
            const frame = frameRef.current;
            if (!frame) return;
            if (typeof window.iFrameResize === "function") {
                window.iFrameResize({ checkOrigin: false }, frame);
                return;
            }
            attempts += 1;
            if (attempts > 40) {
                // Skript nicht erreichbar — feste Höhe, damit das Formular
                // trotzdem bedienbar bleibt statt auf 0 zu kollabieren.
                frame.style.height = fallbackFormHeight;
                return;
            }
            timer = window.setTimeout(attachResizer, 100);
        };
        attachResizer();

        return () => window.clearTimeout(timer);
    }, [isOpenedPopup, fallbackFormHeight]);

    if (!config) return null;

    const {
        headline,
        description,
        buttonText,
        formUrl,
        formTitle
    } = config;

    return (
        <section className="section section-newsletter" id="newsletter">
            <div className="section-newsletter_container">
                <div className="newsletter_content">
                    <div className="newsletter_info" data-aos="fade-right">
                        {headline && <h2 className="newsletter_headline">{headline}</h2>}
                        <p className="newsletter_description">{description}</p>

                        {buttonText && (
                            <div className="newsletter_action">
                                <button type="button" className="newsletter_btn" onClick={openPopupHandler}>
                                    <span className="newsletter_btn_carrot icon-carrot">
                                        <IconCarrot />
                                    </span>
                                    <span className="newsletter_btn_text">{buttonText}</span>
                                </button>
                            </div>
                        )}
                    </div>

                    <div className="newsletter_illustration" data-aos="fade-left">
                        <Lottie className="image_lottie" animationData={PigAnimation} loop={true} />
                    </div>
                </div>
            </div>

            {isOpenedPopup &&
                <Popup closeAction={closePopupHandler} classes="popup-newsletter">
                    <div className="newsletter_embed">
                        <iframe
                            ref={frameRef}
                            data-w-type="embedded"
                            src={formUrl}
                            title={formTitle}
                            width="100%"
                            frameBorder="0"
                            scrolling="no"
                            marginHeight="0"
                            marginWidth="0"
                            style={{ height: 0 }}
                        />
                    </div>
                </Popup>
            }
        </section>
    );
};

export default Newsletter;
